"use client"

import * as React from "react"
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from "embla-carousel-react"
import { ArrowLeft, ArrowRight } from "lucide-react"

import styles from "./carousel.module.css"
import { Button } from "@/components/button/button/button"

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

type CarouselProps = {
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  orientation?: "horizontal" | "vertical"
  setApi?: (api: CarouselApi) => void
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  scrollPrev: () => void
  scrollNext: () => void
  canScrollPrev: boolean
  canScrollNext: boolean
} & CarouselProps

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

function useCarousel() {
  const context = React.useContext(CarouselContext)

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />")
  }

  return context
}

function Carousel({
  orientation = "horizontal",
  opts,
  setApi,
  plugins,
  className,
  children,
  ...props
}: React.ComponentProps<"div"> & CarouselProps) {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === "horizontal" ? "x" : "y",
    },
    plugins
  )
  const [canScrollPrev, setCanScrollPrev] = React.useState(false)
  const [canScrollNext, setCanScrollNext] = React.useState(false)

  const onSelect = React.useCallback((api: CarouselApi) => {
    if (!api) return
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
  }, [])

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev()
  }, [api])

  const scrollNext = React.useCallback(() => {
    api?.scrollNext()
  }, [api])

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault()
        scrollPrev()
      } else if (event.key === "ArrowRight") {
        event.preventDefault()
        scrollNext()
      }
    },
    [scrollPrev, scrollNext]
  )

  React.useEffect(() => {
    if (!api || !setApi) return
    setApi(api)
  }, [api, setApi])

  React.useEffect(() => {
    if (!api) return
    onSelect(api)
    api.on("reInit", onSelect)
    api.on("select", onSelect)

    return () => {
      api?.off("select", onSelect)
    }
  }, [api, onSelect])

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api: api,
        opts,
        orientation:
          orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
      }}
    >
      <div
        onKeyDownCapture={handleKeyDown}
        className={[styles.root, className].filter(Boolean).join(" ")}
        role="region"
        aria-roledescription="carousel"
        data-slot="carousel"
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  )
}

function CarouselContent({ className, ...props }: React.ComponentProps<"div">) {
  const { carouselRef, orientation, scrollNext, scrollPrev, api } = useCarousel()

  const isScrollingRef = React.useRef(false)
  const queuedDirRef = React.useRef<number | null>(null)
  const handleWheel = React.useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      if (orientation !== "horizontal") return
      const absX = Math.abs(event.deltaX)
      const absY = Math.abs(event.deltaY)
      if (absX <= absY) return
      event.preventDefault()
      const dir = event.deltaX > 0 ? 1 : -1
      if (isScrollingRef.current) {
        queuedDirRef.current = dir
        return
      }
      isScrollingRef.current = true

      // Advance exactly one snap, then unlock when Embla settles
      const current = api?.selectedScrollSnap() ?? 0
      const snaps = api?.scrollSnapList().length ?? 0
      const target = Math.max(0, Math.min(snaps - 1, current + dir))
      if (api) {
        api.scrollTo(target)
        const onSettle = () => {
          if (queuedDirRef.current !== null) {
            const followDir = queuedDirRef.current
            queuedDirRef.current = null
            const curr = api.selectedScrollSnap()
            const sz = api.scrollSnapList().length
            const nextTarget = Math.max(0, Math.min(sz - 1, curr + followDir))
            api.scrollTo(nextTarget)
          } else {
            isScrollingRef.current = false
            api.off("settle", onSettle)
          }
        }
        api.on("settle", onSettle)
      } else {
        // Fallback to basic scroll functions
        if (dir > 0) scrollNext(); else scrollPrev();
        window.setTimeout(() => { isScrollingRef.current = false }, 150)
      }
    },
    [orientation, api, scrollNext, scrollPrev]
  )

  return (
    <div
      ref={carouselRef}
      className={styles.contentOuter}
      data-slot="carousel-content"
      onWheel={handleWheel}
    >
      <div
        className={[
          styles.contentInner,
          orientation === "vertical" ? "vertical" : "",
          className,
        ].filter(Boolean).join(" ")}
        {...props}
      />
    </div>
  )
}

function CarouselItem({ className, ...props }: React.ComponentProps<"div">) {
  const { orientation } = useCarousel()

  return (
    <div
      role="group"
      aria-roledescription="slide"
      data-slot="carousel-item"
      className={[
        styles.item,
        orientation === "horizontal" ? "horizontal" : "vertical",
        className,
      ].filter(Boolean).join(" ")}
      {...props}
    />
  )
}

function CarouselPrevious({
  className,
  variant = "default",
  size = "sm",
  ...props
}: React.ComponentProps<typeof Button>) {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel()

  return (
    <Button
      data-slot="carousel-previous"
      variant={variant}
      size={size}
      className={className}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <ArrowLeft />
      <span className={styles.srOnly}>Previous slide</span>
    </Button>
  )
}

function CarouselNext({
  className,
  variant = "default",
  size = "sm",
  ...props
}: React.ComponentProps<typeof Button>) {
  const { orientation, scrollNext, canScrollNext } = useCarousel()

  return (
    <Button
      data-slot="carousel-next"
      variant={variant}
      size={size}
      className={className}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <ArrowRight />
      <span className={styles.srOnly}>Next slide</span>
    </Button>
  )
}

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  useCarousel,
}
